import type { Identifier } from "ra-core";

import { getSupabaseClient } from "./supabase";

/**
 * Almikia Epic E6 (TASK-039): thin typed wrappers over the invoicing RPCs
 * (TASK-038), same pattern as `operations.ts`. The `receivables` and `payables`
 * resources are read through their summary views (see the data provider);
 * only the actions need an RPC. Every function only forwards: the SECURITY
 * DEFINER functions are the authority (role checks, transitions, concept
 * freezing) and raise Spanish messages that are surfaced as-is.
 */
const rpc = async (
  name: string,
  args: Record<string, unknown>,
): Promise<void> => {
  const { error } = await getSupabaseClient().rpc(name, args);
  if (error) {
    console.error(`${name}.error`, error);
    throw new Error(error.message || "No se pudo completar la acción");
  }
};

/** What Dirección may edit while approving a receivable (all optional). */
export type ApproveReceivableOptions = {
  installmentNumber?: number;
  commissionPct?: number;
  /** The subtotal (before IVA); IVA and total are recomputed. */
  amount?: number;
  /** The concept typed by Dirección for a corretaje receivable (it has no project template). */
  conceptOverride?: string;
};

export const createSupabaseInvoicingMethods = () => ({
  /** `approve_receivable()`: Dirección's VoBo; renders and freezes the concept. */
  approveReceivable: (
    id: Identifier,
    options: ApproveReceivableOptions = {},
  ): Promise<void> =>
    rpc("approve_receivable", {
      p_id: id,
      p_installment_number: options.installmentNumber ?? null,
      p_commission_pct: options.commissionPct ?? null,
      p_amount: options.amount ?? null,
      p_concept_override: options.conceptOverride ?? null,
    }),

  /** `reject_receivable()`: returns the receivable to the previous step; the reason is mandatory. */
  rejectReceivable: (id: Identifier, reason: string): Promise<void> =>
    rpc("reject_receivable", { p_id: id, p_reason: reason }),

  /** `mark_receivable_invoiced()`: contabilidad or Dirección records the invoice folio. */
  markReceivableInvoiced: (
    id: Identifier,
    invoiceFolio: string,
  ): Promise<void> =>
    rpc("mark_receivable_invoiced", {
      p_id: id,
      p_invoice_folio: invoiceFolio,
    }),

  /** `mark_receivable_collected()`: `collectedOn` is `YYYY-MM-DD` (today on the server when omitted). */
  markReceivableCollected: (
    id: Identifier,
    collectedOn?: string,
  ): Promise<void> =>
    rpc("mark_receivable_collected", {
      p_id: id,
      p_collected_on: collectedOn ?? null,
    }),

  /** `add_receivable_installment()`: Dirección adds a later installment (a new pendiente_vobo row). */
  addReceivableInstallment: (
    operationId: string,
    installmentNumber: number,
    commissionPct: number,
    amount: number,
  ): Promise<void> =>
    rpc("add_receivable_installment", {
      p_operation_id: operationId,
      p_installment_number: installmentNumber,
      p_commission_pct: commissionPct,
      p_amount: amount,
    }),

  /** `approve_payable()`: Dirección's VoBo of a payable. */
  approvePayable: (id: Identifier): Promise<void> =>
    rpc("approve_payable", { p_id: id }),

  /** `reject_payable()`: returns the payable to the previous step; the reason is mandatory. */
  rejectPayable: (id: Identifier, reason: string): Promise<void> =>
    rpc("reject_payable", { p_id: id, p_reason: reason }),

  /** `mark_payable_paid()`: `paidOn` is `YYYY-MM-DD` (today on the server when omitted). */
  markPayablePaid: (
    id: Identifier,
    paidOn?: string,
    reference?: string,
  ): Promise<void> =>
    rpc("mark_payable_paid", {
      p_id: id,
      p_paid_on: paidOn ?? null,
      p_reference: reference ?? null,
    }),

  /** `set_project_invoice_template()`: Dirección; adds a new template version, issued concepts stay frozen. */
  setProjectInvoiceTemplate: (
    projectId: Identifier,
    template: string,
  ): Promise<void> =>
    rpc("set_project_invoice_template", {
      p_project_id: projectId,
      p_template: template,
    }),

  /** `set_project_developer_commission()`: Dirección sets the project's developer commission %. */
  setProjectDeveloperCommission: (
    projectId: Identifier,
    pct: number,
  ): Promise<void> =>
    rpc("set_project_developer_commission", {
      p_project_id: projectId,
      p_pct: pct,
    }),
});
